import clsx from 'clsx';
import React, { useRef } from 'react';
import { useDispatch } from 'react-redux';

import { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import ModalPortal from '@/src/components/common/Modal/ModalPortal';
import MainToast from '@/src/components/common/Toast/MainToast';
import { isResponseError } from '@/src/components/project/Inbox2/api/config';
import { useToggle } from '@/src/hooks/useToggle';
import { toast } from '@/src/reducers/toast/toast.actions';

import RecordFileStatus from './RecordFiletStatus/RecordFileStatus';
import UndoRecordModal from './UndoRecordModal/UndoRecordModal';

import ActionButton from '../../../../../../Elements/ActionButton/ActionButton';
import { FileEntry, InboxEntryStatus } from '../../../../../../types';
import { useValidateActionResponse } from '../../../../useValidateActionResponse';
import { useFileEntryViewerSidebarActions } from '../../../store/fileEntryViewer.sidebarActions.store';

type RecordFileProps = {
  fileEntry: FileEntry;
};

const RecordFile = ({ fileEntry }: RecordFileProps) => {
  const recordContainerRef = useRef(null);
  const [isPopperOpen, togglePopper] = useToggle(false);
  // @TODO: use useConfirm instead of legacy
  const [showConfirmationModal, toggleShowConfirmationModal] = useToggle(false);
  const { sidebarActions } = useFileEntryViewerSidebarActions();
  const recordFileMutation = sidebarActions.recordFileMutation;
  // const recordFileMutation = useRecordFile({ projectId });
  const dispatch = useDispatch();
  const { confirmRefreshModal } = useValidateActionResponse({
    responseError: isResponseError(recordFileMutation.data) ? recordFileMutation.data : null,
  });

  const handleRecord = async ({ unRecord }) => {
    const isRecorded = unRecord || fileEntry.isRecorded;
    const label = isRecorded ? 'unrecorded' : 'recorded';
    const toastLabel = `Successfully marked entry as ${label}.`;
    const onUndo = !isRecorded ? () => handleRecord({ unRecord: true }) : null;

    if (isRecorded && fileEntry.status === InboxEntryStatus.Closed && !unRecord) {
      toggleShowConfirmationModal();
      return;
    }

    if (unRecord) {
      toggleShowConfirmationModal(false);
    }

    const response = await recordFileMutation.mutateAsync({
      record: !isRecorded,
    });

    // await validateResponse(response);
    if (response?.status) {
      dispatch(toast(<MainToast customClass={`inboxToast`} label={toastLabel} onUndo={onUndo} />));
    }
  };

  const handleMouseEnter = () => {
    togglePopper(true);
  };

  const handleMouseLeave = () => {
    togglePopper(false);
  };

  const { isRecorded } = fileEntry;
  const record = {
    date: fileEntry.recordedDate,
    recordedBy: fileEntry.recordedBy,
  };
  const recordTooltipSetting = !isRecorded
    ? {
        content: 'Record',
        place: 'left',
      }
    : null;

  return (
    <div
      className={clsx(isRecorded ? 'isRecorded' : '')}
      ref={recordContainerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <ActionButton
        id={'record_button'}
        onClick={handleRecord}
        tooltipSettings={recordTooltipSetting}
        icon={ICONS.record_icon({
          mainClassName: clsx(isRecorded && 'tw-bg-cfo-blue-700 tw-rounded-[2px]'),
          className: clsx(
            isRecorded
              ? 'first:tw-fill-cfo-blue-700 tw-fill-white group-hover:tw-fill-white group-hover:first:tw-fill-cfo-blue-700'
              : 'tw-fill-cfo-black group-hover:tw-fill-cfo-blue-700'
          ),
        })}
      />
      {isRecorded && (
        <RecordFileStatus
          record={record}
          open={isPopperOpen}
          anchorEl={recordContainerRef.current}
        />
      )}
      {showConfirmationModal && (
        <ModalPortal>
          <UndoRecordModal
            onConfirm={() => handleRecord({ unRecord: true })}
            onClose={toggleShowConfirmationModal}
          />
        </ModalPortal>
      )}
      {confirmRefreshModal}
    </div>
  );
};

export default RecordFile;
